import React from 'react';
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  HStack,
  IconButton,
  Input,
  NumberInput,
  NumberInputField,
  Select,
  Stack,
  Text,
  Textarea,
  VStack,
} from '@chakra-ui/react';
import { AddIcon, DeleteIcon } from '@chakra-ui/icons';
import { Copy } from 'lucide-react';
import { getCompanyName, Lead, OrderItem } from '../../../utils/types';
import {
  OrderField,
  OrderFulfillmentBadge,
  OrderPaymentBadge,
  OrderSectionCard,
  OrderStatusBadge,
  formatOrderDate,
  formatOrderMoney,
} from './orderUi';

export const createDraftOrderItem = (): OrderItem => ({
  id: `draft-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  product_name: '',
  quantity: 1,
  unit_price: 0,
  total_price: 0,
  price: 0,
});

export const calculateOrderTotals = (items: OrderItem[]) => {
  const subtotal = items.reduce((sum, item) => {
    const lineTotal =
      Number(item.quantity || 0) * Number(item.unit_price ?? item.price ?? 0);
    return sum + lineTotal;
  }, 0);

  return {
    subtotal,
    total: subtotal,
  };
};

export const getOrderRecipient = (lead?: Lead | null) =>
  `${lead?.title ? `${lead.title} ` : ''}${lead?.first_name || ''} ${lead?.second_name || ''}`.trim() ||
  'Guest customer';

export const OrderRecipientSection = ({
  selectedLead,
  selectedLeadId,
  selector,
}: {
  selectedLead?: Lead | null;
  selectedLeadId?: string;
  selector: React.ReactNode;
}) => (
  <OrderSectionCard
    title="Customer"
    description="Choose who this order is for before you finalise products, totals, and delivery details."
    action={selectedLeadId ? <OrderStatusBadge status="draft" /> : undefined}
  >
    <Stack spacing={4}>
      {selector}
      {selectedLead ? (
        <HStack
          justify="space-between"
          align={{ base: 'flex-start', md: 'center' }}
          flexWrap="wrap"
          p={4}
          bg="gray.50"
          borderWidth="1px"
          borderColor="gray.100"
          borderRadius="18px"
        >
          <VStack align="flex-start" spacing={1}>
            <Text fontWeight="800" color="gray.900">
              {getOrderRecipient(selectedLead)}
            </Text>
            <Text fontSize="sm" color="gray.500">
              {getCompanyName(selectedLead.company) || 'No company linked'}
            </Text>
          </VStack>
          <VStack align={{ base: 'flex-start', md: 'flex-end' }} spacing={1}>
            <Text fontSize="sm" color="gray.600">
              {selectedLead.email || 'No email'}
            </Text>
            <Text fontSize="sm" color="gray.600">
              {selectedLead.phone || 'No phone'}
            </Text>
          </VStack>
        </HStack>
      ) : (
        <Text fontSize="sm" color="gray.500">
          Select a lead to connect this order to the right customer and company.
        </Text>
      )}
    </Stack>
  </OrderSectionCard>
);

export const OrderMetaSection = ({
  source,
  status,
  paymentStatus,
  fulfillmentStatus,
  priority,
  requestedDeliveryDate,
  deliveryMethod,
  notes,
  internalNotes,
  deliveryAddress,
  deliveryNotes,
  onChange,
}: {
  source: string;
  status: string;
  paymentStatus: string;
  fulfillmentStatus: string;
  priority: string;
  requestedDeliveryDate: string;
  deliveryMethod: string;
  notes: string;
  internalNotes: string;
  deliveryAddress: string;
  deliveryNotes: string;
  onChange: (field: string, value: string) => void;
}) => (
  <OrderSectionCard
    title="Details"
    description="Keep order status, delivery planning, and internal context in one place."
  >
    <Stack spacing={5}>
      <Stack direction={{ base: 'column', md: 'row' }} spacing={4}>
        <FormControl>
          <FormLabel>Source</FormLabel>
          <Select value={source} onChange={(event) => onChange('source', event.target.value)} borderRadius="16px">
            <option value="wa">WhatsApp</option>
            <option value="fb">Facebook</option>
            <option value="insta">Instagram</option>
            <option value="web">Website</option>
            <option value="phone">Phone</option>
            <option value="other">Other</option>
          </Select>
        </FormControl>
        <FormControl>
          <FormLabel>Status</FormLabel>
          <Select value={status} onChange={(event) => onChange('status', event.target.value)} borderRadius="16px">
            <option value="draft">Draft</option>
            <option value="pending">Pending</option>
            <option value="confirmed">Confirmed</option>
            <option value="processing">Processing</option>
            <option value="shipped">Shipped</option>
            <option value="completed">Completed</option>
            <option value="cancelled">Cancelled</option>
            <option value="returned">Returned</option>
          </Select>
        </FormControl>
      </Stack>

      <Stack direction={{ base: 'column', md: 'row' }} spacing={4}>
        <FormControl>
          <FormLabel>Payment</FormLabel>
          <Select
            value={paymentStatus}
            onChange={(event) => onChange('payment_status', event.target.value)}
            borderRadius="16px"
          >
            <option value="unpaid">Unpaid</option>
            <option value="partially_paid">Partially paid</option>
            <option value="paid">Paid</option>
            <option value="refunded">Refunded</option>
          </Select>
        </FormControl>
        <FormControl>
          <FormLabel>Delivery progress</FormLabel>
          <Select
            value={fulfillmentStatus}
            onChange={(event) => onChange('fulfillment_status', event.target.value)}
            borderRadius="16px"
          >
            <option value="unfulfilled">Not started</option>
            <option value="packed">Packed</option>
            <option value="out_for_delivery">On the way</option>
            <option value="delivered">Delivered</option>
            <option value="returned">Returned</option>
          </Select>
        </FormControl>
        <FormControl>
          <FormLabel>Priority</FormLabel>
          <Select value={priority} onChange={(event) => onChange('priority', event.target.value)} borderRadius="16px">
            <option value="normal">Normal</option>
            <option value="low">Low</option>
            <option value="high">High</option>
            <option value="urgent">Urgent</option>
          </Select>
        </FormControl>
      </Stack>

      <Stack direction={{ base: 'column', md: 'row' }} spacing={4}>
        <FormControl>
          <FormLabel>Requested delivery</FormLabel>
          <Input
            type="date"
            value={requestedDeliveryDate}
            onChange={(event) => onChange('requested_delivery_date', event.target.value)}
            borderRadius="16px"
          />
        </FormControl>
        <FormControl>
          <FormLabel>Delivery method</FormLabel>
          <Select
            value={deliveryMethod}
            onChange={(event) => onChange('delivery_method', event.target.value)}
            borderRadius="16px"
          >
            <option value="pickup">Pickup</option>
            <option value="courier">Courier</option>
            <option value="in_house">In-house delivery</option>
            <option value="other">Other</option>
          </Select>
        </FormControl>
      </Stack>

      <FormControl>
        <FormLabel>Delivery address</FormLabel>
        <Textarea
          value={deliveryAddress}
          onChange={(event) => onChange('delivery_address', event.target.value)}
          borderRadius="16px"
          rows={3}
        />
      </FormControl>

      <Stack direction={{ base: 'column', xl: 'row' }} spacing={4}>
        <FormControl>
          <FormLabel>Customer-facing notes</FormLabel>
          <Textarea
            value={notes}
            onChange={(event) => onChange('notes', event.target.value)}
            borderRadius="16px"
            rows={4}
          />
        </FormControl>
        <FormControl>
          <FormLabel>Internal notes</FormLabel>
          <Textarea
            value={internalNotes}
            onChange={(event) => onChange('internal_notes', event.target.value)}
            borderRadius="16px"
            rows={4}
          />
        </FormControl>
      </Stack>

      <FormControl>
        <FormLabel>Delivery notes</FormLabel>
        <Textarea
          value={deliveryNotes}
          onChange={(event) => onChange('delivery_notes', event.target.value)}
          borderRadius="16px"
          rows={3}
        />
      </FormControl>
    </Stack>
  </OrderSectionCard>
);

export const OrderLineItemsSection = ({
  items,
  products,
  onAddItem,
  onDuplicateItem,
  onChangeItem,
  onRemoveItem,
}: {
  items: OrderItem[];
  products: Array<{ id: string; name: string; price?: number }>;
  onAddItem: () => void;
  onDuplicateItem: (index: number) => void;
  onChangeItem: (
    index: number,
    field: keyof OrderItem,
    value: string | number,
  ) => void;
  onRemoveItem: (index: number) => void;
}) => (
  <OrderSectionCard
    title="Line items"
    description="Add products or services and keep the total in sync as quantity and price change."
    action={
      <Button leftIcon={<AddIcon />} borderRadius="14px" onClick={onAddItem}>
        Add line
      </Button>
    }
  >
    <Stack spacing={4}>
      {items.length === 0 ? (
        <VStack
          py={10}
          px={6}
          borderRadius="20px"
          borderWidth="1px"
          borderColor="gray.100"
          bg="gray.50"
          spacing={3}
          textAlign="center"
        >
          <Text fontWeight="800" color="gray.900">
            No line items yet
          </Text>
          <Text fontSize="sm" color="gray.500" maxW="md">
            Add products or services to build the order and keep pricing clear from the start.
          </Text>
          <Button leftIcon={<AddIcon />} borderRadius="14px" onClick={onAddItem}>
            Add first line
          </Button>
        </VStack>
      ) : (
        items.map((item, index) => {
          const lineTotal =
            Number(item.quantity || 0) * Number(item.unit_price ?? item.price ?? 0);

          return (
            <Box
              key={String(item.id ?? index)}
              borderWidth="1px"
              borderColor="gray.100"
              borderRadius="20px"
              p={{ base: 4, md: 5 }}
              bg="gray.50"
            >
              <Stack spacing={4}>
                <HStack justify="space-between" align="center">
                  <Text fontWeight="700" color="gray.900">
                    Line {index + 1}
                  </Text>
                  <HStack spacing={1}>
                    <IconButton
                      aria-label="Duplicate line"
                      icon={<Copy size={16} />}
                      variant="ghost"
                      size="sm"
                      onClick={() => onDuplicateItem(index)}
                    />
                    <IconButton
                      aria-label="Delete line"
                      icon={<DeleteIcon />}
                      variant="ghost"
                      colorScheme="red"
                      size="sm"
                      onClick={() => onRemoveItem(index)}
                    />
                  </HStack>
                </HStack>

                <Stack direction={{ base: 'column', md: 'row' }} spacing={4}>
                  <FormControl flex={2}>
                    <FormLabel>Product or service</FormLabel>
                    <Select
                      placeholder="Select item"
                      value={item.product_name}
                      onChange={(event) => onChangeItem(index, 'product_name', event.target.value)}
                      bg="white"
                      borderRadius="16px"
                    >
                      {products.map((product) => (
                        <option key={product.id} value={product.name}>
                          {product.name}
                        </option>
                      ))}
                    </Select>
                  </FormControl>

                  <FormControl flex={1}>
                    <FormLabel>Quantity</FormLabel>
                    <NumberInput
                      min={1}
                      value={item.quantity}
                      onChange={(_, valueNumber) =>
                        onChangeItem(index, 'quantity', Number.isNaN(valueNumber) ? 1 : valueNumber)
                      }
                    >
                      <NumberInputField bg="white" borderRadius="16px" />
                    </NumberInput>
                  </FormControl>

                  <FormControl flex={1}>
                    <FormLabel>Unit price</FormLabel>
                    <NumberInput
                      min={0}
                      precision={2}
                      value={Number(item.unit_price ?? item.price ?? 0)}
                      onChange={(_, valueNumber) =>
                        onChangeItem(index, 'unit_price', Number.isNaN(valueNumber) ? 0 : valueNumber)
                      }
                    >
                      <NumberInputField bg="white" borderRadius="16px" />
                    </NumberInput>
                  </FormControl>

                  <FormControl flex={1}>
                    <FormLabel>SKU</FormLabel>
                    <Input
                      value={item.sku || ''}
                      onChange={(event) => onChangeItem(index, 'sku', event.target.value)}
                      bg="white"
                      borderRadius="16px"
                    />
                  </FormControl>
                </Stack>

                <Stack direction={{ base: 'column', lg: 'row' }} spacing={4}>
                  <FormControl flex={3}>
                    <FormLabel>Description</FormLabel>
                    <Textarea
                      value={item.description || ''}
                      onChange={(event) => onChangeItem(index, 'description', event.target.value)}
                      bg="white"
                      borderRadius="16px"
                      rows={2}
                    />
                  </FormControl>
                  <FormControl flex={1}>
                    <FormLabel>Line total</FormLabel>
                    <Input
                      isReadOnly
                      value={formatOrderMoney(lineTotal)}
                      bg="white"
                      borderRadius="16px"
                      fontWeight="700"
                    />
                  </FormControl>
                </Stack>
              </Stack>
            </Box>
          );
        })
      )}
    </Stack>
  </OrderSectionCard>
);

export const OrderSummaryRail = ({
  reference,
  status,
  paymentStatus,
  fulfillmentStatus,
  items,
  requestedDeliveryDate,
  source,
  priority,
}: {
  reference?: string;
  status?: string;
  paymentStatus?: string;
  fulfillmentStatus?: string;
  items: OrderItem[];
  requestedDeliveryDate?: string;
  source?: string;
  priority?: string;
}) => {
  const totals = calculateOrderTotals(items);

  return (
    <OrderSectionCard
      title="Order summary"
      description="Keep the commercial and delivery snapshot visible while you work."
    >
      <Stack spacing={4}>
        <OrderField label="Reference" value={reference || 'Assigned on save'} />
        <HStack justify="space-between" align="center">
          <OrderField label="Status" value={<OrderStatusBadge status={status} />} />
          <OrderField label="Payment" value={<OrderPaymentBadge status={paymentStatus} />} />
        </HStack>
        <OrderField label="Delivery" value={<OrderFulfillmentBadge status={fulfillmentStatus} />} />
        <OrderField
          label="Priority"
          value={priority ? priority.charAt(0).toUpperCase() + priority.slice(1) : 'Normal'}
        />
        <OrderField label="Source" value={source || 'Not set'} />
        <OrderField
          label="Delivery date"
          value={requestedDeliveryDate ? formatOrderDate(requestedDeliveryDate) : 'No date set'}
        />

        <Box pt={2}>
          <Text fontSize="xs" textTransform="uppercase" letterSpacing="0.08em" color="gray.500" mb={1}>
            Subtotal
          </Text>
          <Text fontSize="2xl" fontWeight="800" color="gray.900">
            {formatOrderMoney(totals.subtotal)}
          </Text>
          <Text fontSize="sm" color="gray.500" mt={1}>
            {items.length} line{items.length === 1 ? '' : 's'} in this order
          </Text>
        </Box>

        <Box pt={2} borderTopWidth="1px" borderColor="gray.100">
          <Text fontSize="xs" textTransform="uppercase" letterSpacing="0.08em" color="gray.500" mb={1}>
            Total
          </Text>
          <Text fontSize="3xl" fontWeight="800" color="gray.900">
            {formatOrderMoney(totals.total)}
          </Text>
        </Box>
      </Stack>
    </OrderSectionCard>
  );
};
